import React, { FC, useEffect, useRef, useState } from "react";
import SearchBar from "./SearchBar";
import MobileSearch from "./MobileSearch";
import { useData } from "data/data-provider";
import RenterInfo from "./RenterInfo";
import DriverInfo from "./DriverInfo";
import toast from "react-hot-toast";
import VehicleSwap from "./VehicleSwap";
import { AddCustomer, getCustomer } from "api/customer/customer";
import InsuranceandExtras from "./InsuranceandExtras";
import { getBonzahProducts } from "api/bonzah/bonzah";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import { tripModificationRequest } from "api/booking/booking";
import {
  formatDateAndTimeToISO,
  formatToISO,
  formatDateToHumanFirendly,
  formatTimeToHumanFirendly12Hour,
} from "utils/date-format";
import { getInvoice } from "api/Invoice/invoice";
import Invoice from "designs/Common/(car-search-form)/invoice";
import BookingSuccessModal from "designs/Common/BookingSuccessModal/BookingSuccessModal";

interface EditBookingProps {
  bookingDetails: any;
  startTime: any;
  endTime: any;
  setStartTime: any;
  setEndTime: any;
  fetchBookingDetail: (data?: any) => void;
  onClose?: () => void;
}

const EditBooking: FC<EditBookingProps> = ({
  bookingDetails,
  startTime,
  endTime,
  setStartTime,
  setEndTime,
  fetchBookingDetail,
  onClose,
}) => {
  const [selectedStep, setSelectedStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [invoice, setInvoice] = useState<any>(null);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [invoiceError, setInvoiceError] = useState(false);
  // Surfaced inline at the top of step 0 (Trip Info) when the chosen
  // pickup/dropoff window overlaps fleet unavailability — caught now via a
  // pre-check at step transition so the user doesn't walk all five steps
  // only to be rejected at submit (1NOW-1059 follow-up).
  const [tripInfoError, setTripInfoError] = useState<string | null>(null);
  const [tripInfoChecking, setTripInfoChecking] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [modificationResult, setModificationResult] = useState<any>(null);
  const [isStartDateLocked, setIsStartDateLocked] = useState(false);
  const {
    setBonzahProducts,
    selectedCar,
    startDate,
    endDate,
    pickupLocation,
    dropOffLocation,
    searchedDropoffLocation,
    searchedPickupLoction,
    dropoffLocationType,
    sameDropoffLocation,
    selectedExtras,
    selectedBonzahProducts,
    driverEmail,
    driverFirstName,
    driverLastName,
    driverPhone,
    ourCompany,
  } = useData();

  const stepOptions = [
    "1. Trip Info",
    "2. Vehicle Swap",
    "3. Insurance & Extras",
    "4. Secondary Driver",
    "5. Review",
  ];

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchCustomer = async () => {
      try {
        const response = await getCustomer(bookingDetails?.customer.email);
        if (response) {
          localStorage.setItem("access_token", response.access_token);
          localStorage.setItem("refresh_token", response.refresh_token);
          localStorage.setItem("user", JSON.stringify(response));
        }
      } catch (err) {
        toast.error("An error occurred while processing your request.");
      }
    };
    fetchCustomer();
  }, []);

  const fetchBonzahProducts = async (selectedType?: string) => {
    setLoading(true);
    const payload = {
      company_id: ourCompany?.company,
      selected_type: selectedType,
      fleet: selectedCar?.id,
      pickup_location: pickupLocation?.id,
    };

    const { data, error } = await getBonzahProducts(payload);

    if (data) {
      setBonzahProducts(data);
      setLoading(false);
    } else if (error) {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBonzahProducts();
  }, [selectedCar, pickupLocation]);

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    if (!driverFirstName) newErrors.driverFirstName = "First name is required";
    if (!driverLastName) newErrors.driverLastName = "Last name is required";
    if (!driverEmail) {
      newErrors.driverEmail = "Email is required";
    } else if (
      !/^[\w-]+(\.[\w-]+)*@([\w-]+\.)+[a-zA-Z]{2,7}$/.test(driverEmail)
    ) {
      newErrors.driverEmail = "Please enter a valid email address";
    }
    if (!driverPhone) {
      newErrors.driverPhone = "Phone number is required";
    } else if (driverPhone.length < 10) {
      newErrors.driverPhone = "Please enter a valid phone number";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep = () => {
    if (!startDate || !endDate) {
      toast.error("Please select pick-up and drop-off dates");
      return false;
    }

    if (!pickupLocation) {
      toast.error("Please select a pickup location");
      return false;
    }

    if (sameDropoffLocation === false && !dropOffLocation) {
      toast.error("Please select a drop-off location");
      return false;
    }

    if (
      startDate &&
      endDate &&
      startDate.toDateString() === endDate.toDateString()
    ) {
      if (startTime && endTime) {
        const [startHour, startMinute] = startTime.split(":").map(Number);
        const [endHour, endMinute] = endTime.split(":").map(Number);
        const startTotalMinutes = startHour * 60 + startMinute;
        const endTotalMinutes = endHour * 60 + endMinute;

        if (startTotalMinutes >= endTotalMinutes) {
          toast.error("Drop-off time must be later than pick-up time");
          return false;
        }
      }
    }

    return true;
  };

  const fetchInvoice = async () => {
    if (!selectedCar || !startDate || !endDate || !pickupLocation) return;
    setInvoiceLoading(true);
    const payload = {
      customer: bookingDetails?.customer?.id,
      // 1NOW-1162/#4198: send the booking id so the invoice endpoint can tell
      // the pickup is unchanged and skip the operational-hours check on it
      // (otherwise an unchanged out-of-hours pickup blocks the edit).
      booking: bookingDetails?.id,
      fleet: selectedCar?.id,
      pick_up_time: formatDateAndTimeToISO(startDate, startTime),
      drop_off_time: formatDateAndTimeToISO(endDate, endTime),
      pick_up_location: pickupLocation?.id,
      drop_off_location:
        dropoffLocationType === true ? pickupLocation?.id : dropOffLocation?.id,
      custom_pick_up_address: searchedPickupLoction,
      custom_drop_off_address:
        sameDropoffLocation === false
          ? searchedDropoffLocation
          : searchedPickupLoction,
      same_drop_off_address: sameDropoffLocation,
      // 1NOW-1166: this is an edit — flag it so the invoice endpoint skips the
      // new-booking lead-time ("must be made N hours in advance") and
      // start-time-in-the-past checks against the locked pickup.
      action: "update",
      extras: Array.from(selectedExtras),
      bonzah_insurance_options: Array.from(selectedBonzahProducts),
    };
    const result = await getInvoice(payload);
    if (result?.error) {
      toast.error(result.error);
      setInvoiceError(true);
    } else if (result) {
      setInvoice(result);
      setInvoiceError(false);
    }
    setInvoiceLoading(false);
  };

  const TripModificationRequest = async () => {
    setLoading(true);
    if (driverFirstName || driverLastName || driverEmail || driverPhone) {
      if (!validateForm()) {
        setLoading(false);
        return;
      }

      if (bookingDetails?.customer?.email === driverEmail) {
        toast.error("Customer and secondary driver email cannot be the same");
        setLoading(false);
        return;
      }
      const payload = {
        company: ourCompany?.company.id,
        first_name: driverFirstName,
        last_name: driverLastName,
        email: driverEmail,
        phone_no: driverPhone.replace(/\D/g, ""),
      };
      try {
        const response = await AddCustomer(payload);
        if (response.error) {
          toast.error(response.error);
          setLoading(false);
          return;
        }

        if (response.data) {
          const payload = {
            booking: bookingDetails?.id,
            secondary_driver: response.data.id,
            extras: Array.from(selectedExtras),
            bonzah_insurance_options: Array.from(selectedBonzahProducts),
            fleet: selectedCar?.id,
            pick_up_time: startDate
              ? formatDateAndTimeToISO(startDate, startTime)
              : "",
            drop_off_time: endDate
              ? formatDateAndTimeToISO(endDate, endTime)
              : "",
            pick_up_location: pickupLocation?.id,
            drop_off_location:
              dropoffLocationType === true
                ? pickupLocation?.id
                : dropOffLocation.id,
            custom_pick_up_address: searchedPickupLoction,
            custom_drop_off_address:
              sameDropoffLocation === false
                ? searchedDropoffLocation
                : searchedPickupLoction,
          };
          try {
            const result = await tripModificationRequest(payload);
            if (!result.error) {
              toast.success("Secondary Driver Added successfully");
              setLoading(false);
              // 1NOW-1230: re-fetch with no prefetched arg so the booking
              // detail includes the pending_modification summary the banner
              // renders from (the req_modification response doesn't carry it).
              fetchBookingDetail();
              onClose?.();
            } else {
              toast.error(result.error);
              setLoading(false);
            }
          } catch (err) {
            toast.error("An error occurred while processing your request.");
            setLoading(false);
          }
        }
      } catch {
        toast.error("An error occurred while processing your request.");
        setLoading(false);
      }
    } else {
      setErrors({});
      const payload = {
        booking: bookingDetails?.id,
        extras: Array.from(selectedExtras),
        bonzah_insurance_options: Array.from(selectedBonzahProducts),
        fleet: selectedCar?.id,
        pick_up_time: startDate
          ? formatDateAndTimeToISO(startDate, startTime)
          : "",
        drop_off_time: endDate ? formatDateAndTimeToISO(endDate, endTime) : "",
        pick_up_location: pickupLocation?.id,
        drop_off_location:
          dropoffLocationType === true
            ? pickupLocation?.id
            : dropOffLocation.id,
        custom_pick_up_address: searchedPickupLoction,
        custom_drop_off_address:
          sameDropoffLocation === false
            ? searchedDropoffLocation
            : searchedPickupLoction,
      };

      try {
        const result = await tripModificationRequest(payload);
        if (!result.error) {
          if (result.auto_approved) {
            setModificationResult(result);
            setShowSuccessModal(true);
          } else {
            // Auto-approval was ON but the change couldn't be auto-applied
            // (e.g. no card on file / charge declined) → it fell back to host
            // approval. Don't style that as success; the booking banner's
            // approval_note explains why it's pending.
            if (result.auto_approve_failed) {
              toast.error(
                "Your change couldn't be applied automatically — it's now pending your host's approval.",
              );
            } else {
              toast.success("Your booking modification request has been sent.");
            }
            // 1NOW-1230: re-fetch with no prefetched arg so the booking detail
            // includes the pending_modification summary the "Modification
            // Pending" banner renders from — the req_modification response
            // doesn't carry it, so passing `result` left the banner hidden
            // until a manual refresh.
            fetchBookingDetail();
            onClose?.();
          }
          setLoading(false);
        } else {
          toast.error(result.error);
          setLoading(false);
        }
      } catch (err) {
        toast.error("An error occurred while processing your request.");
        setLoading(false);
      }
    }
  };

  // Pre-flight the chosen pickup/dropoff window against the BE's
  // unavailability check before letting the user advance past Trip Info.
  // Re-uses `tentative_booking_invoice` because that endpoint already runs
  // `exclude_fleets_blocked_by_unavailability` inside `_preprocess_request`,
  // so the same "vehicle unavailable" rejection that would happen at submit
  // surfaces here instead. Empty extras/insurance are fine — the check
  // doesn't depend on them.
  const validateTripInfoUnavailability = async (): Promise<boolean> => {
    if (!selectedCar || !startDate || !endDate || !pickupLocation) {
      return true; // sync validateStep already surfaced these
    }
    setTripInfoChecking(true);
    try {
      const payload = {
        customer: bookingDetails?.customer?.id,
        // 1NOW-1162/#4198: send the booking id so the endpoint can detect an
        // unchanged pickup and skip the operational-hours check on it.
        booking: bookingDetails?.id,
        fleet: selectedCar?.id,
        pick_up_time: formatDateAndTimeToISO(startDate, startTime),
        drop_off_time: formatDateAndTimeToISO(endDate, endTime),
        pick_up_location: pickupLocation?.id,
        drop_off_location:
          dropoffLocationType === true
            ? pickupLocation?.id
            : dropOffLocation?.id,
        custom_pick_up_address: searchedPickupLoction,
        custom_drop_off_address:
          sameDropoffLocation === false
            ? searchedDropoffLocation
            : searchedPickupLoction,
        same_drop_off_address: sameDropoffLocation,
        // 1NOW-1166: edit flow — skip new-booking lead-time / start-time checks
        // on the locked pickup; the fleet-unavailability preflight still runs.
        action: "update",
        extras: [],
        bonzah_insurance_options: [],
      };
      const result = await getInvoice(payload);
      if (result?.error) {
        setTripInfoError(result.error);
        return false;
      }
      setTripInfoError(null);
      return true;
    } finally {
      setTripInfoChecking(false);
    }
  };

  const handleNextStep = async () => {
    if (selectedStep === 0) {
      if (!validateStep()) {
        return;
      }
      const available = await validateTripInfoUnavailability();
      if (!available) {
        return;
      }
    }

    const nextStep = Math.min(selectedStep + 1, stepOptions.length - 1);

    if (nextStep === 4) {
      fetchInvoice();
    }

    setSelectedStep(nextStep);

    if (containerRef.current) {
      containerRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  // Clear the inline Trip Info error whenever the user adjusts any of the
  // inputs that feed into it — so the banner doesn't sit there stale after
  // the user changes the date to a known-available window.
  useEffect(() => {
    if (tripInfoError !== null) setTripInfoError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    startDate,
    endDate,
    startTime,
    endTime,
    pickupLocation?.id,
    dropOffLocation?.id,
    sameDropoffLocation,
    selectedCar?.id,
  ]);

  const handlePreviousStep = () => {
    setSelectedStep((prev) => Math.max(prev - 1, 0));

    if (containerRef.current) {
      containerRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  // 1NOW-1171: up-front "what can change" copy that adapts to the booking's
  // current state (future / in-progress / just-ended), plus the timezone all
  // times on this screen are shown in (from the booking's `timezone` field).
  const nowTime = new Date();
  const pickupInPast = bookingDetails?.pick_up_time
    ? new Date(bookingDetails.pick_up_time) < nowTime
    : false;
  const dropoffInPast = bookingDetails?.drop_off_time
    ? new Date(bookingDetails.drop_off_time) < nowTime
    : false;
  const editScopeMessage = !pickupInPast
    ? "You can change the trip dates, vehicle, insurance, extras, and drivers."
    : dropoffInPast
      ? "Your trip has ended — pick-up and the original drop-off are locked. You can extend the drop-off (within 24h of the trip ending), or update the vehicle, insurance, extras, or drivers."
      : "Your trip has started — the pick-up time is locked. You can still change the drop-off time, vehicle, insurance, extras, or add a driver.";
  const bookingTz = bookingDetails?.timezone || "";
  let tzLabel = "";
  if (bookingTz) {
    try {
      const offset = new Intl.DateTimeFormat("en-US", {
        timeZone: bookingTz,
        timeZoneName: "shortOffset",
      })
        .formatToParts(nowTime)
        .find((p) => p.type === "timeZoneName")?.value;
      tzLabel = `${bookingTz.replace(/_/g, " ")}${offset ? ` (${offset})` : ""}`;
    } catch {
      tzLabel = bookingTz.replace(/_/g, " ");
    }
  }

  // Live "what's changed" diff of the current edit vs the original booking,
  // shown on every step so the renter always sees what they've modified.
  // Times are compared on the wall-clock slice (YYYY-MM-DDTHH:mm) to avoid
  // timezone drift; everything else compares by id.
  const fmtDateTime = (iso?: string | null) =>
    iso
      ? `${formatDateToHumanFirendly(iso)} · ${formatTimeToHumanFirendly12Hour(iso)}`
      : "—";
  const changeRows: { label: string; from: string; to: string }[] = [];

  const origPickupISO = bookingDetails?.pick_up_time as string | undefined;
  const newPickupISO = startDate
    ? formatDateAndTimeToISO(startDate, startTime)
    : null;
  if (
    !isStartDateLocked &&
    origPickupISO &&
    newPickupISO &&
    origPickupISO.slice(0, 16) !== newPickupISO.slice(0, 16)
  ) {
    changeRows.push({
      label: "Pick-up",
      from: fmtDateTime(origPickupISO),
      to: fmtDateTime(newPickupISO),
    });
  }

  const origDropISO = bookingDetails?.drop_off_time as string | undefined;
  const newDropISO = endDate ? formatDateAndTimeToISO(endDate, endTime) : null;
  if (
    origDropISO &&
    newDropISO &&
    origDropISO.slice(0, 16) !== newDropISO.slice(0, 16)
  ) {
    changeRows.push({
      label: "Drop-off",
      from: fmtDateTime(origDropISO),
      to: fmtDateTime(newDropISO),
    });
  }

  if (
    selectedCar?.id &&
    bookingDetails?.fleet?.id &&
    selectedCar.id !== bookingDetails.fleet.id
  ) {
    changeRows.push({
      label: "Vehicle",
      from: bookingDetails.fleet?.name ?? "—",
      to: selectedCar?.name ?? "—",
    });
  }

  const newDropoffLoc =
    dropoffLocationType === true ? pickupLocation : dropOffLocation;
  if (
    pickupLocation?.id &&
    bookingDetails?.pick_up_location?.id &&
    pickupLocation.id !== bookingDetails.pick_up_location.id
  ) {
    changeRows.push({
      label: "Pick-up location",
      from: bookingDetails.pick_up_location?.name ?? "—",
      to: pickupLocation?.name ?? "—",
    });
  }
  if (
    newDropoffLoc?.id &&
    bookingDetails?.drop_off_location?.id &&
    newDropoffLoc.id !== bookingDetails.drop_off_location.id
  ) {
    changeRows.push({
      label: "Drop-off location",
      from: bookingDetails.drop_off_location?.name ?? "—",
      to: newDropoffLoc?.name ?? "—",
    });
  }

  const origExtraIds = ((bookingDetails?.extras || []) as any[])
    .map((e) => (typeof e === "object" && e !== null ? e.id : e))
    .sort();
  const newExtraIds = Array.from(selectedExtras as Set<number>).sort();
  if (JSON.stringify(origExtraIds) !== JSON.stringify(newExtraIds)) {
    changeRows.push({
      label: "Extras",
      from: `${origExtraIds.length} selected`,
      to: `${newExtraIds.length} selected`,
    });
  }

  return (
    <>
      <div
        ref={containerRef}
        className="border border-secondary-100 my-4 p-3 md:p-6"
      >
        <div className="mb-12 px-2 md:px-6 relative">
          <div className="absolute top-[24px] left-[50px] right-[55px] md:left-[70px] md:right-[60px] h-[2px] bg-secondary-100" />
          <div className="absolute top-[24px] h-[2px] ttnc-ButtonPrimary transition-all duration-300" />

          <div className="flex justify-between relative">
            {stepOptions.map((option, idx) => {
              const stepName = option.split(". ")[1];
              const isActive = selectedStep >= idx;

              return (
                <div key={idx} className="flex flex-col items-center">
                  <div
                    className={`w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center mb-2 md:mb-3
                    ${
                      isActive
                        ? "bg-primary-6000 text-text-color"
                        : "bg-white border-2 border-secondary-100 text-secondary-100"
                    }`}
                  >
                    {idx + 1}
                  </div>
                  <span
                    className={`text-xs md:text-sm text-center max-w-[80px] md:max-w-none ${
                      isActive ? "text-primary-6000" : "text-secoondary-100"
                    }`}
                  >
                    {stepName}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* 1NOW-1171: what's editable vs locked for this booking's state + the
            timezone all displayed times are in. */}
        <div className="mb-6 rounded-md border border-primary-200 bg-primary-50 p-3 text-sm text-text-color">
          <p>
            <span className="font-semibold text-primary-6000">
              What you can change:{" "}
            </span>
            {editScopeMessage}
          </p>
          {tzLabel && (
            <p className="mt-1">
              <span className="font-semibold text-primary-6000">
                Times shown in:{" "}
              </span>
              {tzLabel}
            </p>
          )}
        </div>

        {/* Live "what's changed" panel — visible on every step, lists each
            field changed from the original booking (old → new). */}
        {changeRows.length > 0 && (
          <div className="mb-6 rounded-md border border-neutral-200 bg-neutral-50 p-3 text-sm">
            <p className="font-semibold text-text-color mb-2">
              Your changes so far
            </p>
            <div className="space-y-1.5">
              {changeRows.map((row, idx) => (
                <div
                  key={idx}
                  className="flex flex-col sm:flex-row sm:items-baseline gap-0.5 sm:gap-2"
                >
                  <span className="font-medium text-text-color sm:w-40 shrink-0">
                    {row.label}
                  </span>
                  <span className="flex items-center gap-2 flex-wrap">
                    <span className="text-secondary-text-color line-through">
                      {row.from}
                    </span>
                    <span className="text-secondary-text-color">→</span>
                    <span className="text-text-color font-medium">{row.to}</span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {selectedStep === 0 && (
          <>
            {tripInfoError && (
              <div
                role="alert"
                className="mb-4 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-700"
              >
                {tripInfoError}
              </div>
            )}
            <div className="hidden lg:flex">
              <SearchBar
                startTime={startTime}
                setStartTime={setStartTime}
                endTime={endTime}
                setEndTime={setEndTime}
                bookingDetails={bookingDetails}
                isStartDateLocked={isStartDateLocked}
                setIsStartDateLocked={setIsStartDateLocked}
              />
            </div>
            <div className="flex lg:hidden  mt-4">
              <MobileSearch
                startTime={startTime}
                setStartTime={setStartTime}
                endTime={endTime}
                setEndTime={setEndTime}
                bookingDetails={bookingDetails}
                isStartDateLocked={isStartDateLocked}
                setIsStartDateLocked={setIsStartDateLocked}
              />
            </div>
          </>
        )}
        {selectedStep === 1 && (
          <>
            <VehicleSwap
              bookingDetails={bookingDetails}
              setSelected={setSelectedStep}
            />
          </>
        )}
        {selectedStep === 2 && (
          <>
            <InsuranceandExtras bookingDetails={bookingDetails} />
          </>
        )}
        {selectedStep === 3 && (
          <>
            <DriverInfo
              bookingDetails={bookingDetails}
              setErrors={setErrors}
              errors={errors}
            />
          </>
        )}
        {selectedStep === 4 && (
          <div className="max-w-md mx-auto rounded-2xl border border-primary-6000 p-4">
            {invoiceLoading ? (
              <div className="flex justify-center items-center py-10">
                <span className="text-secondary-500">Loading invoice...</span>
              </div>
            ) : invoice ? (
              <Invoice invoice={invoice} carName={selectedCar?.name} />
            ) : (
              <div className="flex justify-center items-center py-10">
                <span className="text-secondary-500">
                  Unable to load invoice
                </span>
              </div>
            )}
          </div>
        )}

        <div className="flex justify-between mt-4">
          <ButtonPrimary
            disabled={selectedStep === 0}
            onClick={handlePreviousStep}
            className="disabled:opacity-50 rounded-md min-w-32"
          >
            Previous
          </ButtonPrimary>
          {selectedStep < stepOptions.length - 1 ? (
            <ButtonPrimary
              onClick={handleNextStep}
              disabled={tripInfoChecking}
              className="disabled:opacity-50 rounded-md min-w-32"
            >
              {tripInfoChecking ? "Checking..." : "Next"}
            </ButtonPrimary>
          ) : (
            <ButtonPrimary
              onClick={TripModificationRequest}
              className="disabled:opacity-50 rounded-md min-w-32"
              disabled={loading || invoiceError}
            >
              {loading ? "Updating..." : "Update"}
            </ButtonPrimary>
          )}
        </div>
      </div>
      <BookingSuccessModal
        isOpen={showSuccessModal}
        onClose={() => {
          setShowSuccessModal(false);
          fetchBookingDetail(modificationResult);
          onClose?.();
        }}
        title="Modification Approved!"
        message="Your booking has been updated and the changes have been applied."
        bookingId={modificationResult?.id ?? bookingDetails?.id}
        uniqueIdentifier={modificationResult?.unique_identifier ?? bookingDetails?.unique_identifier}
        pickUpDate={startDate}
        pickUpTime={startTime}
        dropOffDate={endDate}
        dropOffTime={endTime}
        totalPrice={
          invoice?.total_price_without_security ??
          modificationResult?.invoice?.total_price_without_security
        }
      />
    </>
  );
};

export default EditBooking;
